const INTERNAL_MESSAGE_PATTERNS = [
  /exception/i,
  /cannot invoke/i,
  /nullpointer/i,
  /stack trace/i,
  /internal server error/i,
  /error details:/i,
  /responsehttpstatus/i,
];

const CODE_PRESENTATIONS = {
  insufficient_balance: {
    title: "Wallet balance is too low",
    message: "Add funds to your wallet, then retry this order.",
    action: "recharge",
    actionLabel: "Recharge wallet",
  },
  active_wallet_not_found: {
    title: "Wallet is not ready",
    message: "Your active wallet could not be found. Please refresh your wallet or contact support.",
    action: "wallet",
    actionLabel: "Open wallet",
  },
  merchant_carrier_allocation_rules_not_found: {
    title: "Carrier setup is incomplete",
    message: "Carrier allocation rules have not been configured for your account yet.",
    action: "retry",
    actionLabel: "Try again",
  },
  merchant_carrier_allocation_rules_empty: {
    title: "No carrier rules are available",
    message: "Your account does not currently have any carrier allocation rules.",
    action: "review",
    actionLabel: "Review order",
  },
  no_assignable_allocation_rules_for_order: {
    title: "No carrier rule matches this order",
    message: "Review the route, payment mode, package weight, dimensions, and invoice value.",
    action: "review",
    actionLabel: "Review order",
  },
  no_carrier_matching_preferred_public_name_in_allocation_rule: {
    title: "Preferred carrier is unavailable",
    message: "The preferred carrier in your allocation rule cannot accept this order.",
    action: "review",
    actionLabel: "Review order",
  },
  zone_mapping_not_found: {
    title: "Route is not configured",
    message: "We could not determine a shipping zone for the pickup and delivery pincodes.",
    action: "review",
    actionLabel: "Check pincodes",
  },
  merchant_external_carrier_account_code_mapping_not_found: {
    title: "Carrier account setup is incomplete",
    message: "The selected external carrier account is not linked to your merchant account.",
    action: "review",
    actionLabel: "Review carrier",
  },
  merchant_config_not_found: {
    title: "Merchant setup is incomplete",
    message: "A required merchant configuration is missing. Please try again shortly or contact support.",
    action: "retry",
    actionLabel: "Try again",
  },
  merchant_config_not_correctly_configured: {
    title: "Merchant setup needs attention",
    message: "A required merchant configuration is invalid. Please contact support with the reference below.",
    action: "close",
    actionLabel: "Close",
  },
  create_order_confirmation_status_not_eligible_for_order_processing: {
    title: "Order cannot be processed in its current state",
    message: "Refresh your orders before trying again.",
    action: "orders",
    actionLabel: "View orders",
  },
  invalid_shipment_type_and_payment_mode_combination: {
    title: "Payment mode is not supported",
    message: "This shipment type cannot be booked with the selected payment mode. Review the order type and payment mode.",
    action: "review",
    actionLabel: "Review order",
  },
  invalid_shipment_type_for_create_order_payment_components_calculation: {
    title: "Shipment type is not supported",
    message: "Pricing cannot be calculated for this shipment type.",
    action: "review",
    actionLabel: "Review order",
  },
  carrier_not_serviceable: {
    title: "Carrier is not serviceable",
    message: "The selected carrier cannot service this route.",
    action: "review",
    actionLabel: "Check route",
  },
  operation_not_supported: {
    title: "Carrier does not support this order type",
    message: "Choose a carrier that supports this forward or reverse shipment type.",
    action: "review",
    actionLabel: "Review carrier",
  },
  carrier_config_not_found: {
    title: "Carrier setup is incomplete",
    message: "A required carrier configuration is missing. Please try another carrier or contact support.",
    action: "review",
    actionLabel: "Review carrier",
  },
  carrier_config_not_correctly_configured: {
    title: "Carrier setup needs attention",
    message: "The carrier configuration is invalid. Please try another carrier or contact support.",
    action: "review",
    actionLabel: "Review carrier",
  },
  duplicate_awb_exception: {
    title: "Carrier generated a duplicate tracking number",
    message: "Use a new order reference before retrying this booking.",
    action: "review",
    actionLabel: "Change reference",
  },
  user_not_authorized: {
    title: "You do not have permission",
    message: "Your account is not allowed to create orders. Ask an administrator to update your permissions.",
    action: "close",
    actionLabel: "Close",
  },
};

const ATTEMPT_PRESENTATIONS = {
  "WAE-PPNS": ["Pickup pincode is not serviceable", "pickup_serviceability"],
  "WAE-DPPNS": ["Delivery pincode is not serviceable for prepaid shipments", "drop_serviceability"],
  "WAE-DCPNS": ["Delivery pincode is not serviceable for COD shipments", "drop_serviceability"],
  "WAE-MIVEPP": ["Invoice value exceeds the pickup-route limit", "invoice_limit"],
  "WAE-MIVEDPP": ["Invoice value exceeds the prepaid-route limit", "invoice_limit"],
  "WAE-MIVEDCP": ["Invoice value exceeds the COD-route limit", "invoice_limit"],
  "WAE-IPM": ["Payment mode is not supported", "payment_mode"],
  "WAE-MCPPNF": ["Carrier pricing is not configured", "pricing"],
  "WAE-DPE": ["Carrier temporarily rejected the booking", "carrier_error"],
  "WAE-UE": ["Carrier could not process the shipment details", "carrier_error"],
};

function safeText(value) {
  if (typeof value !== "string") return null;
  const text = value.trim();
  if (!text || text.length > 240 || INTERNAL_MESSAGE_PATTERNS.some((pattern) => pattern.test(text))) return null;
  return text;
}

function normalizeData(source) {
  if (source?.response) {
    return {
      data: source.response.data,
      status: source.response.status,
      networkCode: source.code,
    };
  }
  return { data: source, status: null, networkCode: source?.code };
}

function contextValue(data, snakeKey, camelKey) {
  return data?.context_data?.[snakeKey]
    ?? data?.contextData?.[camelKey]
    ?? data?.contextData?.[snakeKey]
    ?? null;
}

function allocationFailures(data) {
  return contextValue(data, "carrier_allocation_attempt_failures", "carrierAllocationAttemptFailures") || [];
}

function classifyAttempt(failure) {
  const message = `${failure?.error_message || failure?.errorMessage || ""}`.toLowerCase();
  const code = failure?.error_code || failure?.errorCode || "UNKNOWN";
  if (message.includes("active_wallet_not_found")) return ["Active wallet is unavailable", "wallet"];
  if (message.includes("insufficient_balance") || message.includes("insufficient balance")) {
    return ["Insufficient wallet balance", "balance"];
  }
  if (message.includes("invalid_shipment_type_and_payment_mode") || message.includes("invalid payment mode")) {
    return ["Payment mode is not supported for this shipment type", "payment_mode"];
  }
  if (message.includes("operation_not_supported")) {
    return ["Carrier does not support this order type", "order_type"];
  }
  if (message.includes("pricing") && (message.includes("not found") || message.includes("not configured"))) {
    return ["Carrier pricing is not configured", "pricing"];
  }
  if (message.includes("carrier_not_serviceable")) {
    return ["Carrier is not serviceable for this route", "drop_serviceability"];
  }
  if (message.includes("config") && (message.includes("not found") || message.includes("not correctly configured"))) {
    return ["Carrier setup is incomplete", "configuration"];
  }
  return ATTEMPT_PRESENTATIONS[code] || ["Carrier could not accept this order", "carrier_error"];
}

function groupAllocationFailures(failures) {
  const groups = new Map();

  failures.forEach((failure) => {
    const carrier = failure?.carrier_partner || failure?.carrierPartner || "Carrier";
    const [label, category] = classifyAttempt(failure);
    const key = `${category}:${label}`;
    const group = groups.get(key) || { category, label, carriers: new Set(), attempts: 0 };
    group.attempts += 1;
    group.carriers.add(carrier);
    groups.set(key, group);
  });

  return Array.from(groups.values())
    .sort((left, right) => (left.category === "balance" ? -1 : right.category === "balance" ? 1 : 0))
    .map((group) => ({
      category: group.category,
      label: group.label,
      carriers: Array.from(group.carriers),
      attempts: group.attempts,
    }));
}

function allocationIssue(data) {
  const details = groupAllocationFailures(allocationFailures(data));
  const hasBalanceFailure = details.some((detail) => detail.category === "balance");
  const hasWalletFailure = details.some((detail) => detail.category === "wallet");
  const onlyServiceability = details.length > 0 && details.every(
    (detail) => detail.category === "pickup_serviceability" || detail.category === "drop_serviceability",
  );

  if (hasBalanceFailure) {
    return {
      title: "Wallet balance is too low",
      message: "Eligible carriers could not book this order because your wallet does not have enough balance.",
      action: "recharge",
      actionLabel: "Recharge wallet",
      details,
    };
  }
  if (hasWalletFailure) {
    return {
      title: "Wallet is not ready",
      message: "Eligible carriers could not reserve payment because an active wallet was not available.",
      action: "wallet",
      actionLabel: "Open wallet",
      details,
    };
  }
  if (onlyServiceability) {
    return {
      title: "This route is not serviceable",
      message: "None of your allocated carriers currently service this pickup and delivery combination.",
      action: "review",
      actionLabel: "Check pincodes",
      details,
    };
  }
  return {
    title: "No carrier could accept this order",
    message: "Review the reasons below and update the order details before trying again.",
    action: "review",
    actionLabel: "Review order",
    details,
  };
}

function authIssue() {
  return {
    title: "Please sign in again",
    message: "Your session has expired or is no longer valid.",
    action: "login",
    actionLabel: "Sign in",
  };
}

function buildIssue(base, data = {}, status = null) {
  return {
    tone: "error",
    retryable: Boolean(data?.retryable),
    code: data?.code || null,
    correlationId: data?.correlation_id || data?.correlationId || null,
    orderReference: contextValue(data, "order_reference_number", "orderReferenceNumber"),
    details: [],
    ...base,
    status,
  };
}

export function interpretOrderCreationError(source) {
  const { data, status, networkCode } = normalizeData(source);

  if (!source?.response && source instanceof Error) {
    const timeout = networkCode === "ECONNABORTED";
    return buildIssue({
      title: timeout ? "Request timed out" : "Unable to reach Wayuvega",
      message: timeout
        ? "Order creation is taking longer than expected. Check Orders before retrying to avoid a duplicate."
        : "Check your connection and try again.",
      action: timeout ? "orders" : "retry",
      actionLabel: timeout ? "Check orders" : "Try again",
      retryable: !timeout,
    });
  }

  if (typeof data === "string") {
    const lower = data.toLowerCase();
    if (status === 401 || lower.includes("token") || lower.includes("unauthorized")) {
      return buildIssue(authIssue(), {}, status);
    }
    return buildIssue({
      title: "Order could not be created",
      message: "We could not process this order. Please review the details and try again.",
      action: "review",
      actionLabel: "Review order",
    }, {}, status);
  }

  const code = `${data?.code || ""}`.toLowerCase();
  if (status === 401 || code.includes("auth_token") || code.includes("unauthorized_token")) {
    return buildIssue(authIssue(), data, status);
  }
  if (code === "carrier_allocation_unsuccessful_for_order") {
    return buildIssue(allocationIssue(data), data, status);
  }
  if (CODE_PRESENTATIONS[code]) {
    return buildIssue(CODE_PRESENTATIONS[code], data, status);
  }
  if (status === 403) {
    return buildIssue({
      title: "You do not have permission",
      message: "Ask an administrator to grant order creation access.",
      action: "close",
      actionLabel: "Close",
    }, data, status);
  }
  if (status === 429) {
    return buildIssue({
      title: "Too many attempts",
      message: "Wait a moment before trying this order again.",
      action: "retry",
      actionLabel: "Try again",
    }, data, status);
  }
  if (status >= 500 || code === "internal_server_error") {
    return buildIssue({
      title: "We could not create the order",
      message: "A temporary system problem occurred. Check Orders before retrying to avoid a duplicate.",
      action: "orders",
      actionLabel: "Check orders",
    }, data, status);
  }

  return buildIssue({
    title: "Order could not be created",
    message: safeText(data?.message) || "Review the order details and try again.",
    action: data?.retryable ? "retry" : "review",
    actionLabel: data?.retryable ? "Try again" : "Review order",
  }, data, status);
}

export function interpretOrderCreationResponse(data) {
  const metadata = data?.metadata;
  if (!metadata) {
    return { kind: "error", issue: interpretOrderCreationError(data) };
  }
  if (metadata.success && metadata.status !== 102) {
    return { kind: "success", result: data.result };
  }

  const message = `${metadata.message || ""}`;
  const processing = Number(metadata.status) === 102;
  if (processing) {
    const awaitingConfirmation = /awaiting confirmation/i.test(message);
    return {
      kind: "status",
      issue: buildIssue({
        tone: "info",
        title: awaitingConfirmation ? "Order is awaiting confirmation" : "Order is being processed",
        message: awaitingConfirmation
          ? "The order was accepted and will continue after confirmation."
          : "The order was accepted. Check Orders for its latest status before submitting it again.",
        action: "orders",
        actionLabel: "View orders",
      }, metadata, 102),
    };
  }

  if (/new order reference|terminal failure|already exists/i.test(message)) {
    return {
      kind: "error",
      issue: buildIssue({
        title: "Use a new order reference",
        message: "This order reference has already reached a final state. Enter a unique reference number and submit again.",
        action: "review",
        actionLabel: "Change reference",
      }, metadata, metadata.status),
    };
  }

  return {
    kind: "error",
    issue: buildIssue({
      title: "Order could not be created",
      message: safeText(message) || "Review the order details and try again.",
      action: metadata.retryable ? "retry" : "review",
      actionLabel: metadata.retryable ? "Try again" : "Review order",
    }, metadata, metadata.status),
  };
}

// A draft booking that fails now carries the real business error code (OMS DraftPlacementCoreService). Map the codes a
// merchant can act on to a clear, specific line. Anything not here falls back to the message text, then a generic line.
const PLACEMENT_CODE_MESSAGES = {
  insufficient_balance: "Your wallet balance is too low to book this order. Recharge your wallet, then retry.",
  wallet_payment_hold_failed_due_to_insufficient_balance: "Your wallet balance is too low to book this order. Recharge your wallet, then retry.",
  active_wallet_not_found: "Your active wallet could not be found. Refresh your wallet or contact support.",
  carrier_allocation_unsuccessful_for_order: "No carrier could accept this order. Check the route, payment mode, weight, dimensions, and invoice value, then retry.",
  no_assignable_allocation_rules_for_order: "No carrier rule matches this order. Review the route, payment mode, package, and invoice value.",
  merchant_carrier_allocation_rules_not_found: "Carrier allocation rules are not set up for your account yet.",
  merchant_carrier_allocation_rules_empty: "Your account has no carrier allocation rules yet.",
  zone_mapping_not_found: "We could not work out a shipping zone for these pincodes. Check the pickup and delivery pincodes.",
  carrier_not_serviceable: "The carrier cannot service this route.",
  duplicate_awb_exception: "The carrier returned a duplicate tracking number. Use a new order reference before retrying.",
};

export function placementErrorMessage(rawMessage, errorCode) {
  // 1) Prefer the real business code — the draft-booking path carries it, so we can show exact guidance.
  const code = `${errorCode || ""}`.trim().toLowerCase();
  if (code && PLACEMENT_CODE_MESSAGES[code]) return PLACEMENT_CODE_MESSAGES[code];
  if (!rawMessage) return "Could not book this order. Review the order details and try again.";
  // 2) Drafts that failed before the code was carried keep it inside the raw message text — recover it so they read
  //    cleanly too (the raw text itself is never shown; safeText below would suppress it).
  const recovered = Object.keys(PLACEMENT_CODE_MESSAGES).find((c) => rawMessage.toLowerCase().includes(c));
  if (recovered) return PLACEMENT_CODE_MESSAGES[recovered];
  const patterns = [
    [/getOrderInvoiceDetails.*is null/i, "Invoice details are missing. Add the invoice value before booking this order."],
    [/getInvoiceValue.*is null/i, "Invoice value is missing."],
    [/getInvoiceNumber.*is null/i, "Invoice number is missing."],
    [/getDropDetails.*is null/i, "Delivery address details are missing."],
    [/getDropPostalCode.*is null|drop_postal_code.*null/i, "Delivery pincode is missing."],
    [/getDropPoc\b.*is null/i, "Customer name is missing in delivery details."],
    [/getDropPocContactNumber.*is null/i, "Customer phone number is missing in delivery details."],
    [/getDropAddress.*is null/i, "Delivery address is missing."],
    [/getDropCity.*is null/i, "Delivery city is missing."],
    [/getDropState.*is null/i, "Delivery state is missing."],
    [/getPickupDetails.*is null/i, "Pickup address details are missing."],
    [/getPickupPostalCode.*is null/i, "Pickup pincode is missing."],
    [/getPickupAddress.*is null/i, "Pickup address is missing."],
    [/getPickupPoc\b.*is null/i, "Pickup contact person is missing."],
    [/getShipmentDetails.*is null/i, "Shipment details are missing."],
    [/getShipmentPackages.*is null|shipment_packages.*null/i, "Package details are missing."],
    [/getDimensions.*is null/i, "Package dimensions are missing."],
    [/getWeight.*is null/i, "Package weight is missing."],
    [/getOrderDetails.*is null/i, "Order details are missing."],
    [/getOrderType.*is null/i, "Payment mode is missing."],
    [/getOrderReferenceNumber.*is null/i, "Order reference number is missing."],
    [/getItems.*is null|items.*null.*empty/i, "Item details are missing."],
    [/carrier.*not found|carrier.*invalid|getCarrier.*is null/i, "Carrier details are invalid or missing."],
    [/getCodAmount.*is null/i, "COD amount is missing for this order."],
    [/DUPLICATE_ORDER/i, "This order reference already exists. Use a unique reference."],
    [/CARRIER_NOT_SERVICEABLE|PINCODE_NOT_SERVICEABLE/i, "No carrier currently services this route."],
    [/NullPointerException|Cannot invoke.*because.*is null/i, "Some required order details are missing."],
    [/PLACEMENT_EXCEPTION/i, "Could not book this order because required details are missing or invalid."],
  ];
  const match = patterns.find(([pattern]) => pattern.test(rawMessage));
  return match?.[1] || safeText(rawMessage) || "Could not book this order. Review the mandatory fields and try again.";
}

